import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
} from 'react';
import { flushSync } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Building2,
  ChevronLeft,
  Download,
  Loader2,
  MapPin,
  Paperclip,
  ScanLine,
  Trash2,
} from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { JobDescription } from '../../types';
import {
  formatThousands,
  getCaretPositionAfterFormat,
  stripThousandsFormatting,
} from '../../libs/number';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import {
  createJobDescription,
  createRescanJob,
  getJobDescription,
  getOrgUnits,
  postJobDescriptionDownloadUrl,
  updateJobDescription,
  uploadCriteriaAttachment,
  type ApiJobDescriptionDetail,
  type ApiJobDescriptionStatus,
  type ApiJobDescriptionSummary,
  type ApiJobPriority,
  type CriteriaAttachmentRef,
  type ParsedJobDescriptionFields,
} from '../../libs/api/jobDescriptionService';
import { getApiErrorMessage } from '../../libs/api/errorHandler';
import JobTitleCombobox from './JobTitleCombobox';
import { CV_POSITIONS, getCvPositionLabel } from './cvPositions';

export type FormMode = 'create' | 'edit';

export type JobFormState = {
  title: string;
  department: string;
  /** Hiring unit: `d:<id>` department, `g:<id>` group, '' none. */
  orgUnit: string;
  /** Recruited position (CV_POSITIONS value), '' none. Unit + position decide who reviews CVs. */
  position: string;
  level: string;
  type: 'Full-time' | 'Part-time' | 'Contract';
  location: string;
  salaryMin: string;
  salaryMax: string;
  responsibilities: string;
  requirements: string;
  qualifications: string;
  bonusPoints: string;
  contactInformation: string;
  recruitmentInformation: string;
  priority: 'Standard' | 'High' | 'Urgent';
};

export type JobFormErrors = Partial<Record<keyof JobFormState | 'cvThreshold' | 'form', string>>;

export const DEFAULT_CONTACT_INFORMATION =
  'Ứng viên gửi CV cá nhân về địa chỉ mail: HR@dataimpact.vn\nTiêu đề: Họ tên – Vị trí ứng tuyển';

export const DEFAULT_RECRUITMENT_INFORMATION =
  'Để được hỗ trợ thêm thông tin cần thiết, ứng viên vui lòng liên hệ tới phòng Nhân sự của công ty theo số điện thoại: 0592172762 hoặc 0377014585.\nĐịa chỉ: Tầng 3, Tòa nhà New Skyline, Khu Đô thị Văn Quán, Hà Đông.';

export const DEFAULT_JOB_LOCATION =
  'Tầng 3, Tòa nhà New Skyline, Khu đô thị Văn Quán, Hà Đông, Hà Nội';

export const initialJobFormState: JobFormState = {
  title: '',
  department: '',
  orgUnit: '',
  position: '',
  level: 'Junior',
  type: 'Full-time',
  location: DEFAULT_JOB_LOCATION,
  salaryMin: '',
  salaryMax: '',
  responsibilities: '',
  requirements: '',
  qualifications: '',
  bonusPoints: '',
  contactInformation: DEFAULT_CONTACT_INFORMATION,
  recruitmentInformation: DEFAULT_RECRUITMENT_INFORMATION,
  priority: 'Standard',
};

const priorityClass: Record<JobFormState['priority'], string> = {
  Standard: 'border-primary bg-primary-light text-primary',
  High: 'border-warning/30 bg-warning-muted text-warning-muted-foreground',
  Urgent: 'border-error/30 bg-error-muted text-error-muted-foreground',
};

const CRITERIA_DOWNLOAD_SAS_STALE_MS = 30 * 60_000;

const FIELD_LIMITS = {
  title: 255,
  department: 100,
  location: 100,
  responsibilities: 10000,
  requirements: 20000,
  qualifications: 20000,
  bonusPoints: 10000,
  contactInformation: 10000,
  recruitmentInformation: 10000,
} as const;

const DEPARTMENT_GROUPS = [
  {
    label: 'Technology Innovation',
    items: ['Technology Innovation'],
  },
  { label: 'HR', items: ['HR'] },
  { label: 'Accounting', items: ['Accounting'] },
  { label: 'BOD', items: ['BOD'] },
  {
    label: 'Sales',
    items: ['Sales Marketing', 'Sales Japan'],
  },
  {
    label: 'Engineering',
    items: ['Rise Group', 'OpenDA Group', 'Spark Group'],
  },
  { label: 'IT', items: ['IT Group'] },
] as const;

const LEVEL_OPTIONS = ['Intern', 'Fresher', 'Junior', 'Senior'] as const;

const ALL_DEPARTMENT_OPTIONS = DEPARTMENT_GROUPS.flatMap((group) => [group.label, ...group.items]);

const normalizeOptionValue = (value?: string | null): string =>
  (value ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

const mapApiDepartmentToForm = (department?: string | null): string => {
  const rawValue = (department ?? '').trim();
  if (!rawValue) return '';

  const normalized = normalizeOptionValue(rawValue);
  const exactMatch = ALL_DEPARTMENT_OPTIONS.find(
    (item) => normalizeOptionValue(item) === normalized,
  );

  if (exactMatch) {
    return exactMatch;
  }

  const matched = ALL_DEPARTMENT_OPTIONS.find((item) => {
    const normalizedItem = normalizeOptionValue(item);
    return normalizedItem.includes(normalized) || normalized.includes(normalizedItem);
  });

  return matched ?? rawValue;
};

const mapApiLevelToForm = (level?: string | null): JobFormState['level'] => {
  const rawValue = (level ?? '').trim();
  const normalized = normalizeOptionValue(rawValue);

  if (!rawValue) return 'Junior';

  if (normalized.includes('intern')) return 'Intern';
  if (normalized.includes('fresher') || normalized.includes('entry')) {
    return 'Fresher';
  }
  if (
    normalized.includes('junior') ||
    normalized.includes('middle') ||
    normalized.includes('mid')
  ) {
    return 'Junior';
  }
  if (
    normalized.includes('senior') ||
    normalized.includes('lead') ||
    normalized.includes('principal') ||
    normalized.includes('staff')
  ) {
    return 'Senior';
  }

  return rawValue;
};

const parseSalary = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return Number.NaN;
  return parsed;
};

const clampText = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) return value;
  return value.slice(0, maxLength);
};

export const validateByOpenApi = (
  form: JobFormState,
  cvThreshold: number,
  mode: FormMode,
  { requireOrgUnit = true }: { requireOrgUnit?: boolean } = {},
): JobFormErrors => {
  const errors: JobFormErrors = {};

  const title = form.title.trim();
  if (!title) {
    errors.title = 'Job title must not be empty';
  } else if (title.length > FIELD_LIMITS.title) {
    errors.title = `Job title must not exceed ${FIELD_LIMITS.title} characters`;
  }

  const department = form.department.trim();
  if (department.length > FIELD_LIMITS.department) {
    errors.department = `Department must not exceed ${FIELD_LIMITS.department} characters`;
  }

  // Every JD needs a hiring unit and a position: CV reviewers are matched on them. Without an org
  // chart (not configured) the legacy free-text department is used and no unit can be picked.
  if (requireOrgUnit && !form.orgUnit) {
    errors.orgUnit = 'Department / group is required';
  }

  if (!form.position) {
    errors.position = 'Position is required';
  }

  const location = form.location.trim();
  if (location.length > FIELD_LIMITS.location) {
    errors.location = `Location must not exceed ${FIELD_LIMITS.location} characters`;
  }

  const requirements = form.requirements.trim();
  if (mode === 'create' && requirements.length === 0) {
    errors.requirements = 'Requirements must not be empty';
  } else if (requirements.length > 0 && requirements.length < 10) {
    errors.requirements = 'Requirements must be at least 10 characters';
  } else if (requirements.length > FIELD_LIMITS.requirements) {
    errors.requirements = `Requirements must not exceed ${FIELD_LIMITS.requirements} characters`;
  }

  const qualifications = form.qualifications.trim();
  if (mode === 'create' && qualifications.length === 0) {
    errors.qualifications = 'Qualifications must not be empty';
  } else if (qualifications.length > FIELD_LIMITS.qualifications) {
    errors.qualifications = `Qualifications must not exceed ${FIELD_LIMITS.qualifications} characters`;
  }

  const responsibilities = form.responsibilities.trim();
  if (responsibilities.length > FIELD_LIMITS.responsibilities) {
    errors.responsibilities = `Responsibilities must not exceed ${FIELD_LIMITS.responsibilities} characters`;
  }

  const bonusPoints = form.bonusPoints.trim();
  if (bonusPoints.length > FIELD_LIMITS.bonusPoints) {
    errors.bonusPoints = `Bonus points must not exceed ${FIELD_LIMITS.bonusPoints} characters`;
  }

  const contactInformation = form.contactInformation.trim();
  if (contactInformation.length > FIELD_LIMITS.contactInformation) {
    errors.contactInformation = `Contact information must not exceed ${FIELD_LIMITS.contactInformation} characters`;
  }

  const recruitmentInformation = form.recruitmentInformation.trim();
  if (recruitmentInformation.length > FIELD_LIMITS.recruitmentInformation) {
    errors.recruitmentInformation = `Recruitment information must not exceed ${FIELD_LIMITS.recruitmentInformation} characters`;
  }

  if (!Number.isFinite(cvThreshold) || cvThreshold === 0) {
    errors.cvThreshold = 'AI threshold must not be empty';
  } else if (cvThreshold < 0 || cvThreshold > 100) {
    errors.cvThreshold = 'AI threshold must be greater than 0 and less than or equal to 100';
  }

  const salaryMin = parseSalary(form.salaryMin);
  const salaryMax = parseSalary(form.salaryMax);

  if (Number.isNaN(salaryMin)) {
    errors.salaryMin = 'Salary must be a number';
  } else if (salaryMin !== null && salaryMin < 0) {
    errors.salaryMin = 'Salary must be a positive number';
  }

  if (Number.isNaN(salaryMax)) {
    errors.salaryMax = 'Salary must be a number';
  } else if (salaryMax !== null && salaryMax < 0) {
    errors.salaryMax = 'Salary must be a positive number';
  }

  if (
    salaryMin !== null &&
    salaryMax !== null &&
    !Number.isNaN(salaryMin) &&
    !Number.isNaN(salaryMax) &&
    salaryMin > salaryMax
  ) {
    errors.salaryMax = 'Max salary must be greater than or equal to Min salary';
  }

  return errors;
};

const ErrorText = ({ message }: { message?: string }) => {
  if (!message) return null;
  return <p className="mt-1 text-xs text-destructive">{message}</p>;
};

const mapPriorityToApi = (priority: JobFormState['priority']): ApiJobPriority => {
  switch (priority) {
    case 'High':
    case 'Urgent':
      return 'HIGH';
    case 'Standard':
    default:
      return 'MEDIUM';
  }
};

const mapApiPriorityToForm = (priority?: ApiJobPriority): JobFormState['priority'] => {
  if (priority === 'HIGH') return 'High';
  return 'Standard';
};

const mapApiStatusToClosed = (status: ApiJobDescriptionStatus): boolean => status === 'CLOSE';

const mapFormToApiStatus = (isClosed: boolean, asDraft = false): ApiJobDescriptionStatus => {
  if (asDraft) return 'DRAFT';
  return isClosed ? 'CLOSE' : 'ACTIVE';
};

const mapApiContractTypeToFormType = (contractType?: string | null): JobFormState['type'] => {
  if (contractType === 'Part-time') return 'Part-time';
  if (contractType === 'Contract') return 'Contract';
  return 'Full-time';
};

const toOrgUnitValue = (detail: ApiJobDescriptionDetail): string => {
  if (detail.group_id) return `g:${detail.group_id}`;
  if (detail.department_id) return `d:${detail.department_id}`;
  return '';
};

// A group is enough: the backend fills its department.
const toOrgUnitIds = (orgUnit: string) => {
  const [kind, rawId] = orgUnit.split(':');
  const id = Number(rawId);
  const valid = Number.isInteger(id) && id > 0;

  return {
    department_id: valid && kind === 'd' ? id : null,
    group_id: valid && kind === 'g' ? id : null,
  };
};

const mapApiDetailToForm = (detail: ApiJobDescriptionDetail): JobFormState => ({
  title: detail.title ?? '',
  department: mapApiDepartmentToForm(detail.department),
  orgUnit: toOrgUnitValue(detail),
  position: detail.position ?? '',
  level: mapApiLevelToForm(detail.level),
  type: mapApiContractTypeToFormType(detail.contract_type),
  location: detail.location ?? '',
  salaryMin:
    detail.salary_min !== null && detail.salary_min !== undefined ? String(detail.salary_min) : '',
  salaryMax:
    detail.salary_max !== null && detail.salary_max !== undefined ? String(detail.salary_max) : '',
  responsibilities: detail.responsibilities ?? '',
  requirements: detail.requirements ?? '',
  qualifications: detail.qualifications ?? '',
  bonusPoints: detail.bonus_points ?? '',
  contactInformation: detail.contact_information ?? '',
  recruitmentInformation: detail.recruitment_information ?? '',
  priority: mapApiPriorityToForm(detail.priority),
});

const mapParsedJdToForm = (parsed: ParsedJobDescriptionFields): JobFormState => ({
  ...initialJobFormState,
  title: parsed.title ?? '',
  department: mapApiDepartmentToForm(parsed.department),
  location: parsed.location?.trim() || DEFAULT_JOB_LOCATION,
  responsibilities: parsed.responsibilities ?? '',
  requirements: parsed.requirements ?? '',
  qualifications: parsed.qualifications ?? '',
});

export const mapFormToCreatePayload = (
  form: JobFormState,
  cvThreshold: number,
  status: ApiJobDescriptionStatus,
  criteriaAttachment: CriteriaAttachmentRef | null,
) => {
  const salaryMin = parseSalary(form.salaryMin);
  const salaryMax = parseSalary(form.salaryMax);

  return {
    title: form.title.trim(),
    department: form.department.trim() || null,
    ...toOrgUnitIds(form.orgUnit),
    position: form.position || null,
    contract_type: form.type,
    level: form.level,
    location: form.location.trim() || null,
    priority: mapPriorityToApi(form.priority),
    ai_threshold: cvThreshold,
    salary_min: salaryMin === null || Number.isNaN(salaryMin) ? null : salaryMin,
    salary_max: salaryMax === null || Number.isNaN(salaryMax) ? null : salaryMax,
    responsibilities: form.responsibilities.trim() || null,
    requirements: form.requirements.trim(),
    qualifications: form.qualifications.trim(),
    bonus_points: form.bonusPoints.trim() || null,
    contact_information: form.contactInformation.trim() || null,
    recruitment_information: form.recruitmentInformation.trim() || null,
    criteria_attachment: criteriaAttachment,
    status,
  };
};

const buildChangeSnapshot = (
  form: JobFormState,
  cvThreshold: number,
  jobClosed: boolean,
  criteriaAttachment: CriteriaAttachmentRef | null,
  pendingCriteriaFileName = '',
) =>
  JSON.stringify({
    form,
    cvThreshold,
    jobClosed,
    criteriaAttachment,
    pendingCriteriaFileName,
  });

export const buildUpdatePayload = (
  current: ReturnType<typeof mapFormToCreatePayload>,
  initial: ReturnType<typeof mapFormToCreatePayload> | null,
): Partial<ReturnType<typeof mapFormToCreatePayload>> => {
  if (!initial) {
    return current;
  }

  const next: Partial<ReturnType<typeof mapFormToCreatePayload>> = {};
  (Object.keys(current) as Array<keyof typeof current>).forEach((key) => {
    const currentValue = current[key] ?? null;
    const initialValue = initial[key] ?? null;

    if (currentValue !== initialValue) {
      (next as Record<string, unknown>)[key] = currentValue;
    }
  });

  return next;
};

export default function CreateNewJob() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { jdId } = useParams<{ jdId: string }>();
  const mode: FormMode = jdId ? 'edit' : 'create';
  const prefillJd = (location.state as { prefill?: ParsedJobDescriptionFields } | null)?.prefill;

  const [form, setForm] = useState<JobFormState>(() =>
    mode === 'create' && prefillJd ? mapParsedJdToForm(prefillJd) : initialJobFormState,
  );
  const salaryMinInputRef = useRef<HTMLInputElement>(null);
  const salaryMaxInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobClosed, setJobClosed] = useState(false);
  const [criteriaAttachment, setCriteriaAttachment] = useState<CriteriaAttachmentRef | null>(null);
  const [criteriaAttachmentName, setCriteriaAttachmentName] = useState('');
  const [pendingCriteriaFile, setPendingCriteriaFile] = useState<File | null>(null);
  const [isUploadingCriteria, setIsUploadingCriteria] = useState(false);
  const [isDraggingCriteria, setIsDraggingCriteria] = useState(false);
  const [isResolvingCriteriaDownload, setIsResolvingCriteriaDownload] = useState(false);
  const [isRescanning, setIsRescanning] = useState(false);
  const [cvThreshold, setCvThreshold] = useState<number[]>([70]);
  const [errors, setErrors] = useState<JobFormErrors>({});
  const [initialSnapshot, setInitialSnapshot] = useState<string>(() =>
    buildChangeSnapshot(initialJobFormState, 60, false, null, ''),
  );
  const [initialPayload, setInitialPayload] = useState<ReturnType<
    typeof mapFormToCreatePayload
  > | null>(null);

  const { data: orgUnits, isPending: isOrgUnitsPending } = useQuery({
    queryKey: ['org-units'],
    queryFn: getOrgUnits,
    staleTime: 5 * 60 * 1000,
  });

  // Department > Group, flattened for one picker; the label is kept as the JD's department text.
  const orgUnitOptions = useMemo(
    () =>
      (orgUnits?.departments ?? []).flatMap((department) => [
        { value: `d:${department.id}`, label: department.name, depth: 0 },
        ...(orgUnits?.groups ?? [])
          .filter((group) => group.department_id === department.id)
          .map((group) => ({ value: `g:${group.id}`, label: group.name, depth: 1 })),
      ]),
    [orgUnits],
  );
  // Mirrors the backend: a unit is required once the org chart returns units (the legacy
  // department picker is shown when it returns none or fails).
  const requireOrgUnit = isOrgUnitsPending || orgUnitOptions.length > 0;
  const selectedOrgUnitLabel = orgUnitOptions.find(
    (option) => option.value === form.orgUnit,
  )?.label;

  const {
    data: jobDetail,
    error: jobDetailError,
    isLoading: isLoadingDetail,
  } = useQuery({
    queryKey: ['job-description-detail', jdId],
    enabled: mode === 'edit' && Boolean(jdId),
    queryFn: () => getJobDescription(jdId as string),
    staleTime: 15_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (mode !== 'edit' || !jobDetail) return;

    const mapped = mapApiDetailToForm(jobDetail);
    const threshold = jobDetail.ai_threshold ?? 60;
    const isClosed = mapApiStatusToClosed(jobDetail.status);
    const payloadSnapshot = mapFormToCreatePayload(
      mapped,
      threshold,
      jobDetail.status,
      jobDetail.criteria_attachment ?? null,
    );

    setForm(mapped);
    setCvThreshold([threshold]);
    setJobClosed(isClosed);
    setCriteriaAttachment(jobDetail.criteria_attachment ?? null);
    setCriteriaAttachmentName(jobDetail.criteria_attachment?.original_file_name || '');
    setPendingCriteriaFile(null);
    setInitialSnapshot(
      buildChangeSnapshot(mapped, threshold, isClosed, jobDetail.criteria_attachment ?? null, ''),
    );
    setInitialPayload(payloadSnapshot);
  }, [jobDetail, mode]);

  useEffect(() => {
    // Only meant to fire once, right after this page mounts with prefilled content.
    // Deduped via sessionStorage keyed by this navigation entry's key (not a ref
    // or empty dep array) since those don't survive React 18 StrictMode's
    // double-invoke or a Fast Refresh remount in development.
    if (mode !== 'create' || !prefillJd) return;
    const dedupeKey = `jd-prefill-toast:${location.key}`;
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, '1');
    toast.success('JD content loaded from file. Please review before saving.');
  }, []);

  useEffect(() => {
    if (mode !== 'edit' || !jobDetailError) return;
    toast.error(getApiErrorMessage(jobDetailError, 'Failed to load job detail'));
    navigate('/jobs', { replace: true });
  }, [jobDetailError, mode, navigate]);

  useEffect(() => {
    if (mode === 'create') {
      setCriteriaAttachment(null);
      setCriteriaAttachmentName('');
      setPendingCriteriaFile(null);
      setInitialSnapshot(buildChangeSnapshot(initialJobFormState, 60, false, null, ''));
      setInitialPayload(null);
    }
  }, [mode]);

  const isDraftJob = mode === 'edit' && jobDetail?.status === 'DRAFT';
  const pageMode: FormMode = isDraftJob ? 'create' : mode;
  const pageTitle = pageMode === 'edit' ? 'Edit Job' : 'Create New Job';

  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);

  const handleSelectExistingJobTitle = async (summary: ApiJobDescriptionSummary) => {
    setIsApplyingTemplate(true);
    try {
      const detail = await getJobDescription(summary.id);
      const mapped = mapApiDetailToForm(detail);
      setForm(mapped);
      setCvThreshold([detail.ai_threshold ?? 60]);
      setCriteriaAttachment(detail.criteria_attachment ?? null);
      setCriteriaAttachmentName(detail.criteria_attachment?.original_file_name || '');
      setPendingCriteriaFile(null);
      toast.success(`Loaded content from "${detail.title}". Review and edit before saving.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load job description'));
    } finally {
      setIsApplyingTemplate(false);
    }
  };

  const hasChanges = useMemo(
    () =>
      buildChangeSnapshot(
        form,
        cvThreshold[0] ?? 0,
        jobClosed,
        criteriaAttachment,
        pendingCriteriaFile?.name ?? '',
      ) !== initialSnapshot,
    [criteriaAttachment, cvThreshold, form, initialSnapshot, jobClosed, pendingCriteriaFile],
  );

  const currentDepartmentFallback = useMemo(() => {
    const currentValue = form.department.trim();
    if (!currentValue) return null;

    const hasKnownOption = ALL_DEPARTMENT_OPTIONS.some(
      (item) => normalizeOptionValue(item) === normalizeOptionValue(currentValue),
    );

    return hasKnownOption ? null : currentValue;
  }, [form.department]);

  const currentLevelFallback = useMemo(() => {
    const currentValue = form.level.trim();
    if (!currentValue) return null;

    const hasKnownOption = LEVEL_OPTIONS.some(
      (item) => normalizeOptionValue(item) === normalizeOptionValue(currentValue),
    );

    return hasKnownOption ? null : currentValue;
  }, [form.level]);

  const isFormDisabled = pageMode === 'edit' && jobClosed;
  const canTriggerPrimaryJobAction = !isSubmitting && !isFormDisabled;
  const canUploadCriteriaAttachment = canTriggerPrimaryJobAction && !isUploadingCriteria;
  const canRescan =
    mode === 'edit' && Boolean(jdId) && !jobClosed && !isSubmitting && !isRescanning;

  const handleSalaryChange = (
    event: ChangeEvent<HTMLInputElement>,
    field: 'salaryMin' | 'salaryMax',
    inputRef: React.RefObject<HTMLInputElement | null>,
  ) => {
    const input = event.target;
    const caretIndex = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = stripThousandsFormatting(input.value.slice(0, caretIndex)).length;
    const digits = stripThousandsFormatting(input.value);

    flushSync(() => {
      setForm((prev) => ({ ...prev, [field]: digits }));
    });

    const caretPosition = getCaretPositionAfterFormat(formatThousands(digits), digitsBeforeCaret);
    inputRef.current?.setSelectionRange(caretPosition, caretPosition);
  };

  const processCriteriaAttachment = (file: File) => {
    if (!canUploadCriteriaAttachment) {
      toast.error(
        'Criteria attachment can only be selected when Save Draft, Post Job, or Update Job is enabled',
      );
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    const isSupportedFile = extension === 'md' || extension === 'markdown' || extension === 'docx';

    if (!isSupportedFile) {
      toast.error('Only Markdown (.md) or Word (.docx) criteria files are allowed');
      return;
    }

    setPendingCriteriaFile(file);
    setCriteriaAttachmentName(file.name);
    toast.success(
      'Criteria Markdown file selected. It will upload when you save draft, post, or update the job',
    );
  };

  const handleDeleteCriteriaAttachment = () => {
    setPendingCriteriaFile(null);
    setCriteriaAttachment(null);
    setCriteriaAttachmentName('');
    toast.success('Attachment removed');
  };

  const handleDownloadCriteriaAttachment = async () => {
    if (pendingCriteriaFile) {
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(pendingCriteriaFile);
      link.href = objectUrl;
      link.download = pendingCriteriaFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      return;
    }

    if (!jdId) {
      toast.error('No attachment available to download');
      return;
    }

    setIsResolvingCriteriaDownload(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['job-description-download-criteria', jdId],
        queryFn: () => postJobDescriptionDownloadUrl(jdId),
        staleTime: CRITERIA_DOWNLOAD_SAS_STALE_MS,
      });
      if (data?.download_url) {
        window.open(data.download_url, '_blank');
      } else {
        toast.error('No attachment available to download');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to get download link'));
    } finally {
      setIsResolvingCriteriaDownload(false);
    }
  };

  const handleCriteriaAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    processCriteriaAttachment(file);
  };

  const handleCriteriaDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isFormDisabled || isUploadingCriteria || isSubmitting) return;
    setIsDraggingCriteria(true);
  };

  const handleCriteriaDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingCriteria(false);
  };

  const handleCriteriaDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingCriteria(false);

    if (isFormDisabled || isUploadingCriteria || isSubmitting) return;

    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    processCriteriaAttachment(file);
  };

  const showValidationToast = (nextErrors: JobFormErrors, actionLabel: string) => {
    const messages = Object.values(nextErrors).filter((message): message is string =>
      Boolean(message),
    );

    toast.error(`Cannot ${actionLabel} yet`, {
      description: messages[0] || 'Please review the highlighted fields and try again.',
    });
  };

  const validateAndSetErrors = () => {
    const nextErrors = validateByOpenApi(form, cvThreshold[0] ?? 0, pageMode, { requireOrgUnit });

    if (pageMode === 'edit' && !hasChanges) {
      nextErrors.form = 'No changes detected. Update at least one field.';
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showValidationToast(nextErrors, pageMode === 'edit' ? 'update the job' : 'post the job');
      return false;
    }

    return true;
  };

  const saveJob = async (asDraft = false) => {
    let nextCriteriaAttachment = criteriaAttachment;

    if (pendingCriteriaFile) {
      setIsUploadingCriteria(true);
      try {
        nextCriteriaAttachment = await uploadCriteriaAttachment(pendingCriteriaFile);
      } finally {
        setIsUploadingCriteria(false);
      }
    }

    const status = mapFormToApiStatus(jobClosed, asDraft);
    const payload = mapFormToCreatePayload(
      form,
      cvThreshold[0] ?? 60,
      status,
      nextCriteriaAttachment,
    );

    if (mode === 'edit' && jdId) {
      const updatePayload = buildUpdatePayload(payload, initialPayload);

      if (Object.keys(updatePayload).length === 0) {
        setErrors({ form: 'No changes detected. Update at least one field.' });
        return;
      }

      const updated = await updateJobDescription(jdId, updatePayload);
      const mapped = mapApiDetailToForm(updated);
      const nextThreshold = updated.ai_threshold ?? 60;
      const nextClosed = mapApiStatusToClosed(updated.status);
      const nextPayload = mapFormToCreatePayload(
        mapped,
        nextThreshold,
        updated.status,
        updated.criteria_attachment ?? null,
      );

      setForm(mapped);
      setCvThreshold([nextThreshold]);
      setJobClosed(nextClosed);
      setCriteriaAttachment(updated.criteria_attachment ?? null);
      setCriteriaAttachmentName(updated.criteria_attachment?.original_file_name || '');
      setInitialPayload(nextPayload);
      setPendingCriteriaFile(null);
      setInitialSnapshot(
        buildChangeSnapshot(
          mapped,
          nextThreshold,
          nextClosed,
          updated.criteria_attachment ?? null,
          '',
        ),
      );
      queryClient.setQueryData(['job-description-detail', jdId], updated);
      queryClient.invalidateQueries({ queryKey: ['job-descriptions'] });
      setErrors({});

      if (asDraft) {
        toast.success('Draft saved successfully');
        return;
      }

      if (isDraftJob) {
        toast.success('Job posted successfully');
        navigate('/jobs');
        return;
      }

      toast.success('Job updated successfully');
      return;
    }

    const created = await createJobDescription(payload);
    queryClient.invalidateQueries({ queryKey: ['job-descriptions'] });

    if (asDraft) {
      const mapped = mapApiDetailToForm(created);
      const nextThreshold = created.ai_threshold ?? 60;
      const nextClosed = mapApiStatusToClosed(created.status);
      const nextPayload = mapFormToCreatePayload(
        mapped,
        nextThreshold,
        created.status,
        created.criteria_attachment ?? null,
      );

      setForm(mapped);
      setCvThreshold([nextThreshold]);
      setJobClosed(nextClosed);
      setCriteriaAttachment(created.criteria_attachment ?? null);
      setCriteriaAttachmentName(created.criteria_attachment?.original_file_name || '');
      setPendingCriteriaFile(null);
      setInitialPayload(nextPayload);
      setInitialSnapshot(
        buildChangeSnapshot(
          mapped,
          nextThreshold,
          nextClosed,
          created.criteria_attachment ?? null,
          '',
        ),
      );
      setErrors({});
      toast.success('Draft saved successfully');
      return;
    }

    toast.success('Job created successfully');
    navigate('/jobs');
  };

  const handleToggleJobStatus = async () => {
    if (mode !== 'edit' || !jdId) return;

    const nextClosed = !jobClosed;
    const nextStatus = mapFormToApiStatus(nextClosed, false);

    try {
      setIsSubmitting(true);
      const updated = await updateJobDescription(jdId, { status: nextStatus });
      const mapped = mapApiDetailToForm(updated);
      const nextThreshold = updated.ai_threshold ?? cvThreshold[0] ?? 60;
      const latestClosed = mapApiStatusToClosed(updated.status);
      const nextPayload = mapFormToCreatePayload(
        mapped,
        nextThreshold,
        updated.status,
        updated.criteria_attachment ?? null,
      );

      setForm(mapped);
      setCvThreshold([nextThreshold]);
      setJobClosed(latestClosed);
      setCriteriaAttachment(updated.criteria_attachment ?? null);
      setCriteriaAttachmentName(updated.criteria_attachment?.original_file_name || '');
      setInitialPayload(nextPayload);
      setPendingCriteriaFile(null);
      setInitialSnapshot(
        buildChangeSnapshot(
          mapped,
          nextThreshold,
          latestClosed,
          updated.criteria_attachment ?? null,
          '',
        ),
      );
      queryClient.setQueryData(['job-description-detail', jdId], updated);
      queryClient.invalidateQueries({ queryKey: ['job-descriptions'] });
      toast.success(latestClosed ? 'Job closed successfully' : 'Job activated successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update job status'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateAndSetErrors()) return;

    try {
      setIsSubmitting(true);
      await saveJob(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save job'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    const draftErrors = validateByOpenApi(form, cvThreshold[0] ?? 0, pageMode, {
      requireOrgUnit,
    });

    if (Object.keys(draftErrors).length > 0) {
      setErrors(draftErrors);
      showValidationToast(draftErrors, 'save the draft');
      return;
    }

    try {
      setIsSubmitting(true);
      await saveJob(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save draft'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRescan = async () => {
    if (!jdId || !canRescan) {
      return;
    }

    if (hasChanges) {
      toast.error('Please update the job before starting AI re-scan');
      return;
    }

    try {
      setIsRescanning(true);
      const response = await createRescanJob(jdId);
      toast.success('AI re-scan queued successfully', {
        description: `${response.accepted_application_count} application(s) were queued for re-scan.`,
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to start AI re-scan'));
    } finally {
      setIsRescanning(false);
    }
  };

  if (isLoadingDetail) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-bold text-text-main">Loading Job...</h1>
        <p className="text-sm text-text-subtle">Fetching latest job description details.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => navigate('/jobs')}
            className="rounded-full text-text-subtle hover:text-text-main"
          >
            <ChevronLeft size={18} />
          </Button>
          <div>
            <p className="text-sm text-text-subtle uppercase tracking-widest font-semibold">
              Jobs / {pageTitle}
            </p>
            <h1 className="text-4xl font-bold text-text-main mt-1">{pageTitle}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pageMode === 'edit' ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleToggleJobStatus}
                disabled={isSubmitting}
                className={
                  jobClosed
                    ? 'border-success text-success hover:bg-success-muted'
                    : 'border-error/40 text-destructive hover:bg-error-muted'
                }
              >
                {jobClosed ? 'Active Job' : 'Close Job'}
              </Button>
              {!jobClosed ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleRescan()}
                    disabled={!canRescan}
                  >
                    <ScanLine size={14} /> {isRescanning ? 'Re-scanning...' : 'AI Re-scan CV'}
                  </Button>
                  <Button type="submit" form="create-job-form" disabled={isSubmitting}>
                    Update Job
                  </Button>
                </>
              ) : null}
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={handleSaveDraft}>
                Save Draft
              </Button>
              <Button type="submit" form="create-job-form" disabled={isSubmitting}>
                Post Job
              </Button>
            </>
          )}
        </div>
      </div>

      {errors.form ? (
        <div className="rounded-lg border border-error/30 bg-error-muted px-4 py-3 text-sm text-error-muted-foreground">
          {errors.form}
        </div>
      ) : null}

      <form
        id="create-job-form"
        onSubmit={handleSubmit}
        noValidate
        className="grid grid-cols-1 xl:grid-cols-[1.9fr_1fr] gap-5"
      >
        <fieldset disabled={isFormDisabled || isSubmitting} className="contents">
          <div className="space-y-5">
            <section className="bg-card border border-border-subtle rounded-xl p-5 space-y-4">
              <h2 className="text-xl font-bold text-text-main flex items-center gap-2">
                <Briefcase size={16} className="text-primary" /> Job Fundamentals
              </h2>

              <div>
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Job Title <span className="text-destructive">*</span>
                </label>
                {pageMode === 'create' ? (
                  <JobTitleCombobox
                    value={form.title}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        title: clampText(value, FIELD_LIMITS.title),
                      }))
                    }
                    onSelectExisting={(summary) => {
                      void handleSelectExistingJobTitle(summary);
                    }}
                    maxLength={FIELD_LIMITS.title}
                    className="mt-1 bg-bg-main"
                    placeholder="e.g. Senior Product Designer"
                    disabled={isApplyingTemplate}
                  />
                ) : (
                  <Input
                    value={form.title}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        title: clampText(e.target.value, FIELD_LIMITS.title),
                      }))
                    }
                    maxLength={FIELD_LIMITS.title}
                    className="mt-1 bg-bg-main"
                    placeholder="e.g. Senior Product Designer"
                  />
                )}
                <ErrorText message={errors.title} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Position <span className="text-destructive">*</span>
                </label>
                <Select
                  key={`position-${form.position || 'empty'}`}
                  value={form.position}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, position: value }))}
                >
                  <SelectTrigger className="mt-1 bg-bg-main">
                    <span className={form.position ? 'truncate' : 'truncate text-text-subtle'}>
                      {getCvPositionLabel(form.position) || 'Select position'}
                    </span>
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {CV_POSITIONS.map((position) => (
                      <SelectItem key={position.value} value={position.value}>
                        {position.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <ErrorText message={errors.position} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                    Department / Group <span className="text-destructive">*</span>
                  </label>
                  {orgUnitOptions.length > 0 ? (
                    <Select
                      key={`org-unit-${form.orgUnit || 'empty'}`}
                      value={form.orgUnit}
                      onValueChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          orgUnit: value,
                          department:
                            orgUnitOptions.find((option) => option.value === value)?.label ??
                            prev.department,
                        }))
                      }
                    >
                      <SelectTrigger className="mt-1 bg-bg-main">
                        <span
                          className={
                            selectedOrgUnitLabel ? 'truncate' : 'truncate text-text-subtle'
                          }
                        >
                          {selectedOrgUnitLabel || 'Select department / group'}
                        </span>
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        {orgUnitOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            className={option.depth === 0 ? 'font-semibold' : 'pl-6'}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select
                      key={`department-${form.department || 'empty'}`}
                      value={form.department}
                      onValueChange={(v) => setForm((prev) => ({ ...prev, department: v }))}
                    >
                      <SelectTrigger className="mt-1 bg-bg-main">
                        <span
                          className={form.department ? 'truncate' : 'truncate text-text-subtle'}
                        >
                          {form.department || 'Select department'}
                        </span>
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        {currentDepartmentFallback ? (
                          <SelectGroup>
                            <SelectLabel>Current value</SelectLabel>
                            <SelectItem value={currentDepartmentFallback}>
                              {currentDepartmentFallback}
                            </SelectItem>
                          </SelectGroup>
                        ) : null}

                        {DEPARTMENT_GROUPS.map((group) => (
                          <SelectGroup key={group.label}>
                            <SelectLabel className="font-semibold text-text-main">
                              {group.label}
                            </SelectLabel>
                            {group.items.map((item) => (
                              <SelectItem key={item} value={item} className="pl-6">
                                {item}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {orgUnitOptions.length > 0 && !form.orgUnit && form.department ? (
                    <p className="mt-1 text-xs text-warning-muted-foreground">
                      Current: {form.department}. Pick the matching unit so the right reviewers see
                      this JD&apos;s CVs.
                    </p>
                  ) : null}
                  <ErrorText message={errors.orgUnit ?? errors.department} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                    Level
                  </label>
                  <Select
                    key={`level-${form.level || 'empty'}`}
                    value={form.level}
                    onValueChange={(v) => setForm((prev) => ({ ...prev, level: v }))}
                  >
                    <SelectTrigger className="mt-1 bg-bg-main">
                      <span className={form.level ? 'truncate' : 'truncate text-text-subtle'}>
                        {form.level || 'Select level'}
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {currentLevelFallback ? (
                        <SelectGroup>
                          <SelectLabel>Current value</SelectLabel>
                          <SelectItem value={currentLevelFallback}>
                            {currentLevelFallback}
                          </SelectItem>
                        </SelectGroup>
                      ) : null}
                      {LEVEL_OPTIONS.map((level) => (
                        <SelectItem key={level} value={level}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                    Job Type
                  </label>
                  <Select
                    key={`job-type-${form.type || 'empty'}`}
                    value={form.type}
                    onValueChange={(v) =>
                      setForm((prev) => ({
                        ...prev,
                        type: v as JobDescription['type'],
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1 bg-bg-main">
                      <span className={form.type ? 'truncate' : 'truncate text-text-subtle'}>
                        {form.type || 'Select job type'}
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                    Location
                  </label>
                  <div className="relative mt-1">
                    <MapPin
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle"
                    />
                    <Input
                      value={form.location}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          location: clampText(e.target.value, FIELD_LIMITS.location),
                        }))
                      }
                      maxLength={FIELD_LIMITS.location}
                      className="bg-bg-main pl-9"
                      placeholder="Remote or City, State"
                    />
                  </div>
                  <ErrorText message={errors.location} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                    Salary Range
                  </label>
                  <div className="mt-1 grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                    <div className="relative">
                      <Input
                        ref={salaryMinInputRef}
                        inputMode="numeric"
                        value={formatThousands(form.salaryMin)}
                        onChange={(e) => handleSalaryChange(e, 'salaryMin', salaryMinInputRef)}
                        className="bg-bg-main pr-12"
                        placeholder="Min"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-text-subtle">
                        VND
                      </span>
                    </div>
                    <span className="text-text-subtle">-</span>
                    <div className="relative">
                      <Input
                        ref={salaryMaxInputRef}
                        inputMode="numeric"
                        value={formatThousands(form.salaryMax)}
                        onChange={(e) => handleSalaryChange(e, 'salaryMax', salaryMaxInputRef)}
                        className="bg-bg-main pr-12"
                        placeholder="Max"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-text-subtle">
                        VND
                      </span>
                    </div>
                  </div>
                  <ErrorText message={errors.salaryMin || errors.salaryMax} />
                </div>
              </div>
            </section>

            <section className="bg-card border border-border-subtle rounded-xl p-5 space-y-4">
              <h2 className="text-xl font-bold text-text-main flex items-center gap-2">
                <Building2 size={16} className="text-primary" /> Role Description
              </h2>

              <div>
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Responsibilities
                </label>
                <Textarea
                  rows={4}
                  value={form.responsibilities}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      responsibilities: clampText(e.target.value, FIELD_LIMITS.responsibilities),
                    }))
                  }
                  maxLength={FIELD_LIMITS.responsibilities}
                  className="mt-1 bg-bg-main resize-none h-56 max-h-96"
                  placeholder="List the primary duties..."
                />
                <p className="mt-1 text-right text-xs text-text-subtle">
                  {form.responsibilities.length}/{FIELD_LIMITS.responsibilities}
                </p>
                <ErrorText message={errors.responsibilities} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Requirements <span className="text-destructive">*</span>
                </label>
                <Textarea
                  rows={4}
                  value={form.requirements}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      requirements: clampText(e.target.value, FIELD_LIMITS.requirements),
                    }))
                  }
                  maxLength={FIELD_LIMITS.requirements}
                  className="mt-1 bg-bg-main resize-none h-64 max-h-120"
                  placeholder="Skills, experience, and certifications..."
                />
                <p className="mt-1 text-right text-xs text-text-subtle">
                  {form.requirements.length}/{FIELD_LIMITS.requirements}
                </p>
                <ErrorText message={errors.requirements} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Qualifications <span className="text-destructive">*</span>
                </label>
                <Textarea
                  rows={4}
                  value={form.qualifications}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      qualifications: clampText(e.target.value, FIELD_LIMITS.qualifications),
                    }))
                  }
                  maxLength={FIELD_LIMITS.qualifications}
                  className="mt-1 bg-bg-main resize-none h-32 max-h-56"
                  placeholder="Education, degrees, and licenses..."
                />
                <p className="mt-1 text-right text-xs text-text-subtle">
                  {form.qualifications.length}/{FIELD_LIMITS.qualifications}
                </p>
                <ErrorText message={errors.qualifications} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Bonus Points
                </label>
                <Textarea
                  rows={4}
                  value={form.bonusPoints}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      bonusPoints: clampText(e.target.value, FIELD_LIMITS.bonusPoints),
                    }))
                  }
                  maxLength={FIELD_LIMITS.bonusPoints}
                  className="mt-1 bg-bg-main resize-none h-32 max-h-56"
                  placeholder="Additional skills, experience, or achievements..."
                />
                <p className="mt-1 text-right text-xs text-text-subtle">
                  {form.bonusPoints.length}/{FIELD_LIMITS.bonusPoints}
                </p>
                <ErrorText message={errors.bonusPoints} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Contact Information
                </label>
                <Textarea
                  rows={4}
                  value={form.contactInformation}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      contactInformation: clampText(
                        e.target.value,
                        FIELD_LIMITS.contactInformation,
                      ),
                    }))
                  }
                  maxLength={FIELD_LIMITS.contactInformation}
                  className="mt-1 bg-bg-main resize-none h-32 max-h-56"
                  placeholder="How candidates should reach out (email, phone, address)..."
                />
                <p className="mt-1 text-right text-xs text-text-subtle">
                  {form.contactInformation.length}/{FIELD_LIMITS.contactInformation}
                </p>
                <ErrorText message={errors.contactInformation} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Recruitment Information
                </label>
                <Textarea
                  rows={4}
                  value={form.recruitmentInformation}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      recruitmentInformation: clampText(
                        e.target.value,
                        FIELD_LIMITS.recruitmentInformation,
                      ),
                    }))
                  }
                  maxLength={FIELD_LIMITS.recruitmentInformation}
                  className="mt-1 bg-bg-main resize-none h-32 max-h-56"
                  placeholder="Number of openings, application deadline, hiring process notes..."
                />
                <p className="mt-1 text-right text-xs text-text-subtle">
                  {form.recruitmentInformation.length}/{FIELD_LIMITS.recruitmentInformation}
                </p>
                <ErrorText message={errors.recruitmentInformation} />
              </div>
            </section>
          </div>

          <div className="space-y-4">
            <section className="bg-card border border-border-subtle rounded-xl p-5 space-y-4">
              <h2 className="text-sm uppercase tracking-widest text-primary font-bold">
                Posting Settings
              </h2>

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Priority Level
                </label>
                <div className="space-y-2">
                  {(['Standard', 'High', 'Urgent'] as JobFormState['priority'][]).map((lvl) => (
                    <Button
                      key={lvl}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setForm((prev) => ({ ...prev, priority: lvl }))}
                      className={`w-full justify-start ${
                        form.priority === lvl
                          ? priorityClass[lvl]
                          : 'border-border-subtle bg-card text-text-subtle'
                      }`}
                    >
                      {lvl}
                    </Button>
                  ))}
                </div>
              </div>
            </section>

            <section className="bg-card border border-border-subtle rounded-xl p-5 space-y-4">
              <h2 className="text-sm uppercase tracking-widest text-primary font-bold">
                Criteria Attachment
              </h2>

              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                  Upload File
                </label>
                <div
                  className={`rounded-lg border border-dashed bg-bg-main px-3 py-3 text-sm text-text-main transition-colors ${
                    !canUploadCriteriaAttachment
                      ? 'border-border-subtle opacity-60'
                      : isDraggingCriteria
                        ? 'border-primary bg-primary-light/30'
                        : 'border-border-subtle hover:border-primary/50'
                  }`}
                  onDragOver={handleCriteriaDragOver}
                  onDragLeave={handleCriteriaDragLeave}
                  onDrop={handleCriteriaDrop}
                >
                  <div className="flex w-full items-start justify-between gap-2">
                    <label
                      htmlFor="criteria-attachment-input"
                      className={`flex min-w-0 flex-1 items-center gap-2 ${
                        canUploadCriteriaAttachment ? 'cursor-pointer' : 'cursor-not-allowed'
                      }`}
                    >
                      <Paperclip size={14} className="shrink-0 text-primary" />
                      <span className="truncate font-medium">
                        {isUploadingCriteria
                          ? 'Uploading criteria file...'
                          : criteriaAttachmentName || 'Upload criteria file (.md or .docx)'}
                      </span>
                    </label>

                    {criteriaAttachmentName ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => {
                            void handleDownloadCriteriaAttachment();
                          }}
                          disabled={
                            !criteriaAttachmentName ||
                            isUploadingCriteria ||
                            isResolvingCriteriaDownload
                          }
                          className="text-text-subtle hover:bg-transparent hover:text-primary"
                          aria-label="Download attachment"
                          title="Download attachment"
                        >
                          {isResolvingCriteriaDownload ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Download size={14} />
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          onClick={handleDeleteCriteriaAttachment}
                          disabled={!canUploadCriteriaAttachment}
                          className="text-destructive hover:bg-transparent hover:text-destructive/80"
                          aria-label="Delete attachment"
                          title="Delete attachment"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  <label
                    htmlFor="criteria-attachment-input"
                    className={`mt-2 block text-xs text-text-subtle ${
                      canUploadCriteriaAttachment ? 'cursor-pointer' : 'cursor-not-allowed'
                    }`}
                  >
                    {pendingCriteriaFile
                      ? 'Attachment selected. It will be uploaded when you click Save Draft, Post Job, or Update Job.'
                      : criteriaAttachmentName
                        ? 'Attachment uploaded. You can download or delete it.'
                        : 'Drag and drop a Markdown (.md) or Word (.docx) file here, or click to browse.'}
                  </label>
                  <input
                    id="criteria-attachment-input"
                    type="file"
                    accept=".md,.markdown,.docx,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    disabled={!canUploadCriteriaAttachment}
                    onChange={handleCriteriaAttachmentChange}
                  />
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-widest text-text-subtle font-bold">
                    CV Approval Threshold <span className="text-destructive">*</span>
                  </label>
                  <span className="text-sm font-bold text-primary">{cvThreshold[0] ?? 0}</span>
                </div>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={cvThreshold}
                  disabled={isFormDisabled}
                  onValueChange={setCvThreshold}
                />
                <ErrorText message={errors.cvThreshold} />
              </div>
            </section>
          </div>
        </fieldset>
      </form>
    </div>
  );
}
